import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDateTime } from "@/lib/format";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiPaths, api } from "@/lib/api";
import { postToHost, useEmbedHostBridge } from "@/lib/embedHost";
import type { Session } from "@/lib/types";
import { BackLink } from "@/components/BackLink";
import { TopBar } from "@/components/TopBar";
import { useSessionBus } from "@/lib/sessionBus";
import { AuditBadge } from "@/modules/audit";
import { endSessionReturnPath } from "@/modules/sessions/endSessionReturn";
import { ChatPanel } from "@/modules/chat";
import { ExportBundleModal } from "@/modules/exports";
import { OperatorRoom, OperatorToolbar, RecordingControl } from "@/modules/room";
import {
  BeneficiaryContextPanel,
  ConferenceCapBanner,
  GpsStatusBadge,
  InspectionQaPanel,
  InspectionReportPanel,
  InviteFieldUserModal,
  ParticipantCountBadge,
  ParticipantsSidebarPanel,
  SessionJoinLinkCard,
} from "@/modules/sessions";

export default function SessionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [inviteOpen, setInviteOpen] = useState(false);
  // "Invite an observer" opens the same modal in observer mode: role
  // fixed, and it leads with the people already in the org.
  const [inviteMode, setInviteMode] = useState<"field" | "observer">("field");
  const [exportOpen, setExportOpen] = useState(false);

  // Backlog 2026-09-22 — a scheduled session used to render a STATIC
  // "scheduled" placeholder with no polling, so an operator who opened
  // the page before the slot never saw it go live: the beat flipped
  // `status` to `open` server-side at the appointment time, this query
  // never refetched, and the beneficiary joined an empty room while the
  // operator stared at a placeholder. Navigating away and back was the
  // only way out — which is exactly how it was reported. Poll while the
  // session is still pre-live, then stop.
  const session = useQuery({
    queryKey: ["session", id],
    queryFn: async () => (await api.get<Session>(ApiPaths.session(id!))).data,
    enabled: !!id,
    refetchInterval: (q) => {
      const d = q.state.data;
      if (!d) return false;
      return d.status === "created" && d.scheduled_for ? 15_000 : false;
    },
  });

  // The operator may want to be in the room BEFORE the slot (the
  // reported flow was joining ~2 minutes early to be set up). The
  // scheduled placeholder offers an explicit "join now" that drops
  // them straight in rather than making them wait or reload.
  const [joinEarly, setJoinEarly] = useState(false);

  // ONE answer to "is this session live yet", because the page was
  // giving two. `joinEarly` let the room mount before the slot, but the
  // recording control, the Q&A panel and the GPS badge each asked
  // `isScheduledFuture` on its own and so stayed hidden.
  //
  // Reported 2026-09-28 from a live call: "the beneficiary joined
  // before their 9:00 appointment. I could see them on camera, but
  // because they joined early it was not possible to record the
  // conversation or access the questions." The operator was in the room
  // with the beneficiary in front of them and no way to do the
  // inspection.
  //
  // The server never had this restriction — `SessionRecording.start`
  // gates on consent, not on the clock — so this was the UI refusing
  // something the backend would happily have done.
  const beforeSlot = session.data
    ? isScheduledFuture(session.data) && !joinEarly
    : false;

  // `isScheduledFuture` is a pure clock comparison, so nothing
  // re-evaluates it on its own once rendered. Re-render exactly when
  // the slot arrives so the room mounts on time even if the poll above
  // hasn't landed yet.
  const [, setClockTick] = useState(0);
  const scheduledForMs = session.data?.scheduled_for
    ? new Date(session.data.scheduled_for).getTime()
    : null;
  const sessionStatus = session.data?.status;
  useEffect(() => {
    if (sessionStatus !== "created" || scheduledForMs === null) return;
    const delay = scheduledForMs - Date.now();
    if (delay <= 0) return;
    const timer = setTimeout(() => setClockTick((n) => n + 1), delay + 500);
    return () => clearTimeout(timer);
  }, [sessionStatus, scheduledForMs]);

  const endSession = useMutation({
    mutationFn: async () => {
      // Broadcast first so the field's data channel sees the end signal
      // even if the LiveKit Server-API delete_room call fails on the
      // backend. The bus lives in useSessionBus (populated by
      // InRoomAnnotation when the operator's room is up).
      const bus = useSessionBus.getState().bus;
      if (bus) {
        try {
          await bus.send({
            topic: "system",
            type: "session.ended",
            payload: { reason: "operator ended" },
          });
        } catch {
          /* best-effort — backend's delete_room is the durable signal */
        }
      }
      const res = await api.post<Session>(ApiPaths.sessionEnd(id!));
      return res.data;
    },
    onSuccess: () => {
      // Disconnect operator-side too — otherwise the local LiveKit
      // client keeps the camera open until the token expires.
      const room = useSessionBus.getState().room;
      void room?.disconnect();
      void qc.invalidateQueries({ queryKey: ["sessions"] });
      // Phase 1B-I — let the embed host know the session finished
      // so their `onSessionComplete(sessionId)` callback fires
      // before we navigate away.
      if (id) {
        postToHost({
          source: "nb-iframe",
          type: "session-completed",
          sessionId: id,
        });
      }
      // Back where the operator came from, not the dashboard. A
      // campaign inspection returns to that campaign's beneficiary
      // list (the next person to call is right there); a standalone
      // one returns to the inspection list. Landing on the dashboard
      // meant re-navigating after every single call.
      navigate(endSessionReturnPath({ campaign: session.data?.campaign }), {
        replace: true,
      });
    },
  });

  // Ending an inspection is irreversible — it closes the room, stops
  // the recording and starts the billing close. It used to fire on a
  // single click, and since the End button moved onto the video
  // (#284) it is far easier to hit by accident.
  const [confirmEnd, setConfirmEnd] = useState(false);

  // Mount the embed-iframe command bridge so host pages can drive
  // recording lifecycle via `widget.startRecording()` etc.
  // No-op when the SPA isn't embedded (window.parent === window).
  useEmbedHostBridge({ sessionId: id ?? "" });

  return (
    <div className="flex h-screen flex-col">
      <TopBar />
      <div className="flex flex-1 overflow-hidden">
        <main className="relative flex-1">
          {session.data ? (
            isTerminalStatus(session.data.status) ? (
              <SessionEndedView session={session.data} />
            ) : beforeSlot ? (
              <SessionScheduledView
                session={session.data}
                onJoinNow={() => setJoinEarly(true)}
              />
            ) : (
              <OperatorRoom
                sessionId={session.data.id}
                livekitRoomSid={session.data.livekit_room_sid}
                inRoomControls={<OperatorToolbar sessionId={session.data.id} />}
                // Same mutation the sidebar button fires — handed down
                // rather than reimplemented, so there is exactly one
                // way to end a session.
                endSlot={
                  !isTerminalStatus(session.data.status) ? (
                    <button
                      type="button"
                      className="btn-danger shrink-0 text-xs"
                      onClick={() => setConfirmEnd(true)}
                      disabled={endSession.isPending}
                      data-testid="overlay-end-session"
                    >
                      {endSession.isPending
                        ? t("session.endingSession")
                        : t("session.endSession")}
                    </button>
                  ) : undefined
                }
                onInviteFieldUser={() => {
                  setInviteMode("field");
                  setInviteOpen(true);
                }}
              />
            )
          ) : (
            <p className="p-6 text-nb-text-2">{t("session.loadingSession")}</p>
          )}
          {/* Inspection Q&A — floating bottom-right overlay over the
              video stage. Renders only when the campaign has questions
              (the panel returns null otherwise) AND the session is
              live (not scheduled/ended). Default-collapsed so it
              doesn't obstruct the field-side feed until the operator
              opens it.

              Backlog 2026-09-21 — z-40, not z-20: OperatorToolbar (the
              bottom-center recording/zoom/annotation pill) floats at
              z-30, with its own popovers also at z-30 and per-button
              tooltips at z-40. At z-20 this panel sat BEHIND all of
              that, so expanding it near the toolbar made its own
              options unclickable — the toolbar was intercepting the
              clicks. z-40 clears the toolbar and its popovers, and
              stays below the app-wide `.modal-backdrop` (z-50) so a
              real dialog (e.g. invite field user) still wins if both
              happen to be open at once.

              2026-09-28 — and `bottom-20`, not `bottom-4`. The media
              control bar (mic / camera / share / END SESSION) was later
              floated at `bottom-4 left-1/2`, and with French labels it
              grows wide enough for its right end to run underneath this
              panel. Being z-40 over the bar's z-30, the panel then
              covered the End button outright: reported from a live
              call as "when there is Q&A she can't click end
              inspection".

              Raising the bar above the panel would only swap which
              element is unreachable, so the fix is spatial — this band
              belongs to the controls, and the panel sits above it. */}
          {session.data &&
            session.data.campaign &&
            !isTerminalStatus(session.data.status) &&
            !beforeSlot && (
              <div
                data-testid="qa-panel-floating-wrapper"
                className="pointer-events-none absolute bottom-20 right-4 z-40 max-w-full"
              >
                <div className="pointer-events-auto">
                  <InspectionQaPanel sessionId={session.data.id} floating />
                </div>
              </div>
            )}
        </main>
        {/* `overflow-y-auto` + `shrink-0` are load-bearing on short
            viewports (13" laptops ≈ 700px of usable height). The parent
            row is `overflow-hidden`, so without a scroller here the
            sidebar's lower half — invite button, report panel, End
            session — is clipped and UNREACHABLE rather than scrollable.
            `shrink-0` stops the video stage squeezing the rail narrower
            than its base width.

            Width steps up on larger viewports (backlog §9.2, 2026-09-08):
            the fixed 20rem rail was reported unusable on a real desktop
            monitor — chat + evidence cards + participants + recording
            controls all cramped into one narrow column regardless of how
            much spare width the video stage wasn't using. Stays at 20rem
            below Tailwind's `xl` (1280px) breakpoint — laptop-class
            viewports keep the original layout — then 28rem at `xl` and
            30rem at `2xl` (1536px). No drag-resize, no toggle: three
            shapes were on the table (breakpoint bump / drag-resize /
            "focus chat" mode) and this one was chosen for shipping today
            without new interaction/state to test. */}
        <aside className="flex w-80 shrink-0 flex-col gap-3 overflow-y-auto border-l border-nb-border bg-nb-bg/80 p-4 xl:w-[28rem] 2xl:w-[30rem]">
          {session.data && (
            <>
              <div className="space-y-1">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
                  {t("nav.sessions").replace(/s$/, "")}
                </h2>
                <p className="text-xs text-nb-text-3">{session.data.id}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="pill">{session.data.status}</span>
                  <AuditBadge sessionId={session.data.id} />
                  <ParticipantCountBadge
                    sessionId={session.data.id}
                    maxParticipants={session.data.max_participants}
                  />
                  <GpsStatusBadge
                    sessionId={session.data.id}
                    active={
                      !isTerminalStatus(session.data.status) && !beforeSlot
                    }
                  />
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-nb-text-2">
                  <Link
                    to={`/sessions/${session.data.id}/audit`}
                    className="hover:text-nb-mint hover:underline"
                  >
                    {t("session.auditChain")} →
                  </Link>
                </div>
                {!isTerminalStatus(session.data.status) && !beforeSlot && (
                    <RecordingControl
                      sessionId={session.data.id}
                      status={session.data.status}
                      serverRecordingStartedAt={
                        session.data.recording_started_at
                      }
                    />
                  )}
              </div>

              {/* Redesign 2026-09-23 — for a campaign session the join
                  link now lives INSIDE the beneficiary card, next to the
                  person it is addressed to. A standalone session has no
                  beneficiary card to put it in, so it keeps its own. */}
              {!isTerminalStatus(session.data.status) &&
                !(session.data.campaign && session.data.membership) && (
                  <SessionJoinLinkCard
                    sessionId={session.data.id}
                    onInvite={() => {
                      setInviteMode("field");
                      setInviteOpen(true);
                    }}
                  />
                )}

              {/* Phase invites-sidebar — participants moved from a
                  separate route into a collapsible disclosure right
                  next to chat. Pending invites get Resend / Edit /
                  Cancel inline; joined participants surface here for
                  context (DSR actions still live on the standalone
                  admin page). Hidden affordances once terminal. */}
              <ParticipantsSidebarPanel
                sessionId={session.data.id}
                terminal={isTerminalStatus(session.data.status)}
                hideFieldUsers={
                  !!session.data.campaign && !!session.data.membership
                }
              />

              {/* Beneficiary reference card — identity/address/legal-entity
                  details + custom fields (membership- and contact-scoped),
                  so the operator can see what was specified while asking
                  Q&A questions during the call. Only present for
                  campaign-linked sessions (standalone sessions have no
                  membership to look up). */}
              {session.data.campaign && session.data.membership && (
                <BeneficiaryContextPanel
                  campaignId={session.data.campaign}
                  membershipId={session.data.membership}
                  sessionId={
                    isTerminalStatus(session.data.status)
                      ? undefined
                      : session.data.id
                  }
                  onInvite={() => {
                    setInviteMode("field");
                    setInviteOpen(true);
                  }}
                />
              )}

              {/* Chat is now the primary narrative surface — text, evidence
                  cards (auto-posted from snapshot + recording ingest), and
                  system notices share one chronological thread.

                  `flex-1` lets it absorb spare vertical space on a tall
                  screen; `min-h-[16rem]` stops it being the shock
                  absorber for everything else on a short one. It used to
                  carry `min-h-0` here, which let the block compress to
                  ~23px on a 13" laptop — the messages were still mounted,
                  just squeezed to nothing. The inner wrapper keeps
                  `min-h-0` so ChatPanel's own list is what scrolls. */}
              <div className="flex min-h-[16rem] flex-1 flex-col">
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-nb-text-2">
                  {t("session.chat")}
                </h3>
                <div className="min-h-0 flex-1">
                  <ChatPanel
                    sessionId={session.data.id}
                    readOnly={
                      isTerminalStatus(session.data.status) || beforeSlot
                    }
                  />
                </div>
              </div>

              <ConferenceCapBanner session={session.data} />

              <div className="space-y-2">
                {session.data.scheduled_for && (
                  <div className="rounded-md border border-nb-border bg-slate-900/40 px-3 py-2 text-xs">
                    <span className="text-nb-text-2">{t("session.scheduled")}: </span>
                    <span className="font-medium text-nb-text">
                      {formatDateTime(session.data.scheduled_for)}
                    </span>
                    {session.data.notes && (
                      <p className="mt-1 whitespace-pre-wrap text-nb-text-2">
                        {session.data.notes}
                      </p>
                    )}
                  </div>
                )}
                {/* "Invite field user" used to sit here too, opening the
                    very same modal as the one in the joining-info block
                    above. Two buttons, same action, a screen apart —
                    and on a campaign session it read as nonsense: the
                    beneficiary IS the field user, so the sidebar
                    appeared to be asking you to invite a second one.
                    The joining block is where it belongs, next to the
                    link it mints. Removed 2026-09-24.

                    "Invite an observer" stays: an observer is a third
                    party, so it is genuinely a session-level action and
                    has nowhere else to live. Small, because it is
                    occasional. */}
                {!isTerminalStatus(session.data.status) && (
                  <button
                    type="button"
                    className="w-full text-center text-xs text-nb-text-3 underline-offset-2 hover:text-nb-mint hover:underline"
                    onClick={() => {
                      setInviteMode("observer");
                      setInviteOpen(true);
                    }}
                    data-testid="invite-observer-link"
                  >
                    {t("session.inviteObserver", "Invite an observer")}
                  </button>
                )}
                {/* Phase 2 — Report + Signed Report + Export bundle are
                    post-session artefacts. Showing them mid-session was
                    confusing (the chain isn't finalised yet, so the
                    signed PDF would carry an incomplete tail hash and
                    the export bundle wouldn't include the final
                    composite recording). Gate on `isTerminalStatus` so
                    these only appear after the operator has ended the
                    session. */}
                {isTerminalStatus(session.data.status) && (
                  <>
                    <InspectionReportPanel sessionId={session.data.id} />
                    <button
                      className="btn-ghost w-full"
                      onClick={() => setExportOpen(true)}
                      data-testid="open-export-modal"
                    >
                      {t("session.exportBundle")}
                    </button>
                  </>
                )}
              </div>

              {!isTerminalStatus(session.data.status) && (
                <button
                  className="btn-danger w-full"
                  onClick={() => setConfirmEnd(true)}
                  disabled={endSession.isPending}
                  data-testid="sidebar-end-session"
                >
                  {endSession.isPending
                    ? t("session.endingSession")
                    : t("session.endSession")}
                </button>
              )}
            </>
          )}
        </aside>
      </div>
      {confirmEnd && session.data && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="end-session-title"
          className="modal-backdrop bg-black/70"
        >
          <div className="modal-panel w-full max-w-sm space-y-3">
            <h2 id="end-session-title" className="text-base font-semibold">
              {t("session.confirmEndTitle")}
            </h2>
            <p className="text-sm text-nb-text-2">
              {t("session.confirmEndBody")}
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                className="btn-ghost text-sm"
                onClick={() => setConfirmEnd(false)}
                disabled={endSession.isPending}
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="btn-danger text-sm"
                autoFocus
                onClick={() => endSession.mutate()}
                disabled={endSession.isPending}
                data-testid="confirm-end-session"
              >
                {endSession.isPending
                  ? t("session.endingSession")
                  : t("session.endSession")}
              </button>
            </div>
          </div>
        </div>
      )}

      {exportOpen && session.data && (
        <ExportBundleModal
          sessionId={session.data.id}
          onClose={() => setExportOpen(false)}
        />
      )}

      {inviteOpen && session.data && (
        <InviteFieldUserModal
          sessionId={session.data.id}
          mode={inviteMode}
          onClose={() => setInviteOpen(false)}
        />
      )}
    </div>
  );
}


/** Terminal states from `apps.sessions.models.SessionStatus`. Sessions
 *  in these states have no LiveKit room to join + no operator actions
 *  to drive; the SPA renders a read-only summary instead of mounting
 *  OperatorRoom. Mirrors the backend's `TERMINAL_SESSION_STATUSES`
 *  set so the invite / remind / resend gates align across the wire. */
function isTerminalStatus(status: Session["status"]): boolean {
  return status === "closed" || status === "expired" || status === "cancelled";
}


/** True when the session is scheduled for a moment in the future and
 *  hasn't been touched yet. Sessions in this state must NOT mount the
 *  LiveKit room — the operator would otherwise camera-on / mint a
 *  token / start the audit chain hours or days before the inspection
 *  actually starts. Status `created` is the only one this gate fires
 *  on; once recording starts (or someone joins), the status moves to
 *  `open` / `recording` and this returns false even if scheduled_for
 *  is still in the future. */
function isScheduledFuture(session: Session): boolean {
  if (session.status !== "created") return false;
  if (!session.scheduled_for) return false;
  return new Date(session.scheduled_for).getTime() > Date.now();
}


/** Main-area replacement for OperatorRoom when the session has ended.
 *  No LiveKit token mint, no camera, no recording control — just a
 *  clear "this session is over" banner and quick links to the
 *  artefacts (audit chain, media, participants). The sidebar's chat
 *  panel still renders the message history in read-only mode. */
function SessionEndedView({ session }: { session: Session }) {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
      <div className="absolute left-4 top-4">
        <BackLink
          fallback={session.campaign ? `/campaigns/${session.campaign}` : "/sessions"}
          className="btn-ghost text-sm"
        >
          ← {t("common.back")}
        </BackLink>
      </div>
      <div className="rounded-full bg-slate-700/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-nb-text-2">
        {session.status === "expired"
          ? t("session.expiredBadge")
          : t("session.endedBadge")}
      </div>
      <h2 className="text-xl font-semibold text-nb-text">
        {t("session.endedTitle")}
      </h2>
      <p className="max-w-md text-sm text-nb-text-2">
        {t("session.endedBody")}
      </p>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-2 text-sm">
        <Link to={`/sessions/${session.id}/media`} className="btn-ghost">
          {t("sessionsList.actionMedia")}
        </Link>
        <Link to={`/sessions/${session.id}/audit`} className="btn-ghost">
          {t("session.auditChain")}
        </Link>
      </div>
    </div>
  );
}


/** Main-area replacement for OperatorRoom when the session is
 *  scheduled for a future moment. No LiveKit token mint, no camera —
 *  just a clear "scheduled for X" panel so the operator can see
 *  what they booked and use the sidebar to invite the field user or
 *  edit the booking. Once the scheduled time arrives (or the
 *  operator manually flips the session to live), the SessionPage
 *  re-renders against the live status and mounts OperatorRoom. */
function SessionScheduledView({
  session,
  onJoinNow,
}: {
  session: Session;
  /** Enter the room ahead of the scheduled slot — the operator is the
   *  host and routinely wants to be set up before the beneficiary
   *  arrives. */
  onJoinNow?: () => void;
}) {
  const { t } = useTranslation();
  const when = session.scheduled_for
    ? formatDateTime(session.scheduled_for, {
        dateStyle: "full",
        timeStyle: "short",
      })
    : "";
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
      <div className="absolute left-4 top-4">
        <BackLink
          fallback={session.campaign ? `/campaigns/${session.campaign}` : "/sessions"}
          className="btn-ghost text-sm"
        >
          ← {t("common.back")}
        </BackLink>
      </div>
      <div className="rounded-full bg-nb-mint/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-nb-mint">
        {t("session.scheduledBadge")}
      </div>
      <h2 className="text-xl font-semibold text-nb-text">
        {t("session.scheduledTitle")}
      </h2>
      <p className="max-w-md text-sm text-nb-text-2">
        {t("session.scheduledBody")}
      </p>
      {when && (
        <p className="text-base font-medium text-nb-text">{when}</p>
      )}
      {session.notes && (
        <p className="max-w-md whitespace-pre-wrap rounded-md border border-nb-border bg-slate-900/40 px-3 py-2 text-left text-xs text-nb-text-2">
          {session.notes}
        </p>
      )}
      {onJoinNow && (
        <button type="button" className="btn-primary text-sm" onClick={onJoinNow}>
          {t("session.joinNowEarly", "Enter the room now")}
        </button>
      )}
    </div>
  );
}